// web/src/player/wordTimingRepair.ts
// Client-side guards for server-produced WordTiming[] (TTS → STT → NW alignment). The
// aligner has documented failure modes (see the 2026-07-05 hardening audit): the final
// word's STT end can undershoot the real audio end (no floor exists server-side when the
// last word IS matched), and interpolation can bunch words. These helpers repair/reconcile
// on the client, where the decoded audio's real duration is available.
import type { WordTiming } from "./types";

export const END_SIGNAL = {
  /** Photo audio is voice-only with at most a brief TTS trailing pause — any larger gap
   *  between last-word end and the file end means the timings undershot. */
  PHOTO_TAIL_SEC: 0.75,
  /** voiceDeck DELIBERATELY pads ~2.5s of outro-tail silence after the last word
   *  (functions/src/voiceDeck.ts OUTRO_TAIL_SEC) so the outro can finish before onEnded —
   *  the deck allowance must cover it plus slack. */
  DECK_TAIL_SEC: 3.0,
  /** End signals must land strictly before the real audio end, or natural end (which
   *  cancels the rAF loop) beats them and the ending is dropped. */
  END_EPS_SEC: 0.15,
} as const;

/**
 * The time the END SIGNALS (character outro, completion stinger, caption blank) should
 * fire at — wordTimings[last].end reconciled against the decoded audio's real duration.
 *
 * - duration unknown → trust the raw timings (today's behavior).
 * - raw end within the tail allowance of duration → trust it (deck's padded tail lands here).
 * - raw end far short of duration (STT undershoot) → stretch to duration - tail, so the
 *   character doesn't flip to its outro while the voice is still talking.
 * - raw end at/past duration (overshoot) → clamp below duration, so the outro/stinger/blank
 *   still fire before natural end cancels the rAF loop (no more dropped endings).
 */
/** Client-side floor for a repaired word's width (server floors are 0.03/0.05s). */
const MIN_WORD_SEC = 0.02;

// auditWordTimings thresholds: a run of BUNCH_MIN_RUN consecutive words each at/below
// BUNCH_MAX_WORD_SEC is the alignment-interpolation bunching signature; GAP_SLACK pads the
// per-path tail allowance before an end-gap is worth flagging.
const BUNCH_MIN_RUN = 5;
const BUNCH_MAX_WORD_SEC = 0.06;
const GAP_SLACK_SEC = 0.5;
// A first word starting deeper than this into the audio is the prompt-narration signature
// (observed 2026-07-05: Gemini-TTS spoke its director-notes prompt; the aligned script
// words therefore begin far into the clip). Normal TTS lead-in is well under a second.
const LEAD_MAX_SEC = 2.5;

/**
 * Diagnostics only — human-readable warnings about timing-invariant violations, logged by
 * the player as `[timings]` breadcrumbs so intermittent "odd playback" reports come with
 * evidence. Run on the RAW server timings (before ingest normalization) so the log shows
 * what was actually received. Never changes behavior.
 */
export function auditWordTimings(
  timings: WordTiming[],
  { durationSec, isDeck = false }: { durationSec?: number; isDeck?: boolean } = {},
): string[] {
  if (timings.length === 0) return [];
  const warnings: string[] = [];

  let degenerate = 0;
  for (const w of timings) {
    if (!isFinite(w.start) || !isFinite(w.end) || w.start < 0 || w.end <= w.start) degenerate++;
  }
  if (degenerate > 0) {
    warnings.push(`${degenerate} degenerate word timing(s) (non-finite, negative, or start>=end) — repaired on ingest`);
  }

  for (let i = 1; i < timings.length; i++) {
    if (timings[i].start < timings[i - 1].start) {
      warnings.push("unsorted word timings — sorted on ingest");
      break;
    }
  }

  const firstStart = timings.reduce((m, w) => (isFinite(w.start) ? Math.min(m, w.start) : m), Infinity);
  if (isFinite(firstStart) && firstStart > LEAD_MAX_SEC) {
    warnings.push(
      `first word starts ${firstStart.toFixed(1)}s into the audio (TTS may have vocalized non-script content — prompt narration?)`,
    );
  }

  let run = 0;
  let runStart = 0;
  let bunchFlagged = false;
  for (const w of timings) {
    const width = w.end - w.start;
    if (isFinite(width) && width >= 0 && width <= BUNCH_MAX_WORD_SEC) {
      if (run === 0) runStart = w.start;
      run++;
      if (run >= BUNCH_MIN_RUN && !bunchFlagged) {
        warnings.push(
          `bunched cluster of ${BUNCH_MIN_RUN}+ near-zero words at ~${runStart.toFixed(1)}s (alignment interpolation artifact — captions snap through it)`,
        );
        bunchFlagged = true;
      }
    } else {
      run = 0;
    }
  }

  if (durationSec !== undefined && isFinite(durationSec) && durationSec > 0) {
    const rawEnd = timings.reduce((m, w) => (isFinite(w.end) ? Math.max(m, w.end) : m), 0);
    const tail = isDeck ? END_SIGNAL.DECK_TAIL_SEC : END_SIGNAL.PHOTO_TAIL_SEC;
    const gap = durationSec - rawEnd;
    if (gap > tail + GAP_SLACK_SEC) {
      warnings.push(`last word ends ${gap.toFixed(1)}s before the audio end (STT end undershoot — end signals reconciled)`);
    } else if (gap < -0.05) {
      warnings.push(`last word ends ${(-gap).toFixed(1)}s past the audio end (end signals clamped)`);
    }
  }
  return warnings;
}

/**
 * Backstop for the invariants the player silently assumes but the server never enforces:
 * finite times, non-negative starts, start < end, sorted ascending by start. Returns the
 * INPUT ARRAY UNCHANGED when it is already clean (the common case) so memoized consumers
 * don't churn. Never mutates the input.
 */
export function normalizeWordTimings(timings: WordTiming[]): WordTiming[] {
  let dirty = false;
  const cleaned: WordTiming[] = [];
  for (const w of timings) {
    if (!isFinite(w.start) || !isFinite(w.end)) { dirty = true; continue; }
    let start = w.start;
    let end = w.end;
    if (start < 0) { start = 0; dirty = true; }
    if (end < start + MIN_WORD_SEC) { end = start + MIN_WORD_SEC; dirty = true; }
    cleaned.push(start === w.start && end === w.end ? w : { ...w, start, end });
  }
  for (let i = 1; i < cleaned.length && !dirty; i++) {
    if (cleaned[i].start < cleaned[i - 1].start) dirty = true;
  }
  if (!dirty) return timings;
  return cleaned.sort((a, b) => a.start - b.start);
}

export function reconcileDialogueEnd(
  timings: WordTiming[],
  durationSec: number | undefined,
  isDeck: boolean,
): number {
  if (timings.length === 0) return 0;
  const raw = timings[timings.length - 1].end;
  if (durationSec === undefined || !isFinite(durationSec) || durationSec <= 0) return raw;
  const tail = isDeck ? END_SIGNAL.DECK_TAIL_SEC : END_SIGNAL.PHOTO_TAIL_SEC;
  const stretched = Math.max(raw, durationSec - tail);
  return Math.max(0, Math.min(durationSec - END_SIGNAL.END_EPS_SEC, stretched));
}
